
import { Vue, Component, Prop, VModel, Watch } from "vue-property-decorator";
import { ROOT_ACTIONS } from "@/store/actions";
import { ActualActivity } from "@/types";
import { User } from "@/store/user/types";
import { USER_GETTERS } from "@/store/user/getters";
import { Timestamp, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { firestore } from "@/firebase";
import { parse, format, subMinutes } from "date-fns";

@Component
export default class EditActualActivity extends Vue {
  @VModel({ required: true }) show!: boolean;
  @Prop({ required: true }) actualActivity!: ActualActivity;
  @Prop({ required: false, default: false }) disableSkipActivityReminders!: boolean;

  get user(): User {
    return this.$store.getters[USER_GETTERS.ACTING_USER];
  }

  get isCommentRequired(): boolean {
    return this.user.type === 'COACH' || this.user.type === 'ADMIN';
  }

  get commentRules(): Array<(value: string) => boolean | string> {
    if (!this.isCommentRequired) {
      return [];
    }

    return [(value: string) => !!value?.trim() || (this.$t('commentRequired') as string)];
  }

  step = 1;
  loading = false;

  deleting = false;

  inputs = {
    id: "",
    hours: 1,
    minutes: -1,
    comment: "",
    date: format(
      subMinutes(new Date(), new Date().getTimezoneOffset()),
      "yyyy-MM-dd"
    ),
    time: "08:00",
    participantPresent: true,
    participantConfirmed: true,
    skipActivityReminders: false,
  };

  @Watch("actualActivity", { immediate: true })
  onChange() {
    this.inputs.id = this.actualActivity.id as string;
    this.inputs.hours = Math.floor(this.actualActivity.actualHours);
    this.inputs.minutes = Math.ceil(
      60 * (this.actualActivity.actualHours - this.inputs.hours)
    );
    this.inputs.comment = this.actualActivity.comment;
    this.inputs.participantPresent = this.actualActivity.participantPresent;
    const d = this.actualActivity.date.toDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    this.inputs.date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    this.inputs.time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    this.inputs.participantConfirmed = this.actualActivity.participantConfirmed;
    this.inputs.skipActivityReminders =
      !!this.actualActivity.skipActivityReminders;
  }

  resetFields() {
    this.inputs = {
      id: "",
      hours: 1,
      minutes: -1,
      comment: "",
      date: format(
        subMinutes(new Date(), new Date().getTimezoneOffset()),
        "yyyy-MM-dd"
      ),
      time: "08:00",
      participantPresent: true,
      participantConfirmed: true,
      skipActivityReminders: false,
    };
  }

  async submit() {
    if (this.isCommentRequired) {
      const commentField = this.$refs.commentField as Vue & { validate: () => boolean };
      if (!commentField?.validate()) {
        return;
      }
    }

    try {
      this.loading = true;
      const actualDate = parse(
        `${this.inputs.date} ${this.inputs.time}`,
        "yyyy-MM-dd HH:mm",
        new Date()
      );

      await updateDoc(
        doc(firestore, "actual-activities", this.actualActivity.id as string),
        {
          actualHours: this.inputs.hours + this.inputs.minutes / 60,
          comment: this.inputs.comment,
          date: Timestamp.fromDate(actualDate),
          participantConfirmation: null,
          participantPresent: this.inputs.participantPresent,
          participantConfirmed: this.inputs.participantConfirmed || false,
          skipActivityReminders: this.inputs.skipActivityReminders,
        }
      );

      this.show = false;
      this.$emit("refresh");
      this.resetFields();
      this.step = 1;
    } catch (error: any) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: error.message || error,
      });
    } finally {
      this.loading = false;
    }
  }

  async deleteActualActivity() {
    try {
      this.deleting = true;

      await deleteDoc(
        doc(firestore, "actual-activities", this.actualActivity.id as string)
      );
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "success",
        text: "Verwijderd",
      });
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: error,
      });
    } finally {
      this.$emit("refresh");
      this.deleting = false;
      this.show = false;
    }
  }
}
